import React, { useContext, useEffect, useState, useCallback } from "react";
import { LoadingIcon, TopDropdown } from "../../../components/index.js";
import { getGreenhouses, getHarvestedPlots } from "../../../utils/api.js";
import { UserContext } from "../../../context/UserContext.jsx";
import { useLocalStorage } from "../../../hooks/index.js";
import dayjs from "dayjs";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";

const extractNumber = (value) => {
  const match = String(value || "").match(/(\d+)/);
  return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
};

const isValidBackendDate = (s) => {
  if (!s) return false;
  return dayjs(String(s), BACKEND_DATE_FORMAT, true).isValid();
};

const buildHarvestMap = (harvestRoot) => {
  const map = new Map();
  harvestRoot?.greenhouses?.forEach((gh) => {
    gh?.plots?.forEach((p) => {
      map.set(`${gh.id}-${p.id}`, {
        harvested_date: p.harvested_date ?? null,
        total_yield: p.total_yield ?? null,
        total_weight: p.total_weight ?? null,
        weight_per_tub: p.weight_per_tub ?? null,
        weight_per_tub_source: p.weight_per_tub_source ?? null,
        ploughed_under_reasons: p.ploughed_under_reasons ?? [],
        ploughed_under_note: p.ploughed_under_note ?? null,
      });
    });
  });
  return map;
};

const mergeHarvestIntoGreenhouses = (
  greenhouseList,
  harvestRoot,
  { preserveExisting = false, clearMissingKeys = null } = {},
) => {
  const harvestMap = buildHarvestMap(harvestRoot);

  return (greenhouseList || []).map((g) => {
    const plots = (g.plots || []).map((p) => {
      const key = `${g.id}-${p.id}`;
      const h = harvestMap.get(key);

      // ✅ Not returned by backend
      if (!h) {
        // If we are preserving existing, keep untouched EXCEPT
        // when this plot key is explicitly part of the refresh subset.
        if (preserveExisting) {
          if (clearMissingKeys && clearMissingKeys.has(key)) {
            return {
              ...p,
              harvested_date: null,
              total_yield: null,
              total_weight: null,
              weight_per_tub: null,
              weight_per_tub_source: null,
              ploughed_under_reasons: [],
              ploughed_under_note: null,
            };
          }
          return p;
        }

        // default behavior (fetchGreenhouses path)
        return {
          ...p,
          harvested_date: null,
          total_yield: null,
          total_weight: null,
          weight_per_tub: null,
          weight_per_tub_source: null,
          ploughed_under_reasons: [],
          ploughed_under_note: null,
        };
      }

      const harvested_date = h.harvested_date ?? null;

      return {
        ...p,
        harvested_date,
        total_yield:
          harvested_date && isValidBackendDate(harvested_date)
            ? (h.total_yield ?? 0)
            : null,
        total_weight:
          harvested_date && isValidBackendDate(harvested_date)
            ? (h.total_weight ?? 0)
            : null,
        weight_per_tub: h.weight_per_tub ?? null,
        weight_per_tub_source: h.weight_per_tub_source ?? null,
        ploughed_under_reasons: h.ploughed_under_reasons ?? [],
        ploughed_under_note: h.ploughed_under_note ?? null,
      };
    });

    return { ...g, plots };
  });
};

const decorateAndFilterOld = (greenhouseList, oldDaysThreshold) => {
  const enriched = (greenhouseList || []).map((g) => {
    const plots = g.plots || [];
    const hasPlots = plots.length > 0;

    const isFullyHarvested =
      hasPlots && plots.every((p) => isValidBackendDate(p.harvested_date));

    const latestHarvestDate = (() => {
      const dates = plots
        .map((p) => p.harvested_date)
        .filter((d) => isValidBackendDate(d));
      if (!dates.length) return null;

      dates.sort(
        (a, b) =>
          dayjs(b, BACKEND_DATE_FORMAT).valueOf() -
          dayjs(a, BACKEND_DATE_FORMAT).valueOf(),
      );
      return dates[0];
    })();

    const daysSinceLatest =
      latestHarvestDate && isValidBackendDate(latestHarvestDate)
        ? dayjs().diff(dayjs(latestHarvestDate, BACKEND_DATE_FORMAT), "day")
        : null;

    const isOldHarvested =
      isFullyHarvested &&
      (oldDaysThreshold > 0
        ? (daysSinceLatest ?? -1) >= oldDaysThreshold
        : false);

    return {
      ...g,
      plots,
      _isFullyHarvested: isFullyHarvested,
      _latestHarvestDate: latestHarvestDate,
      _daysSinceLatestHarvest: daysSinceLatest,
      _isOldHarvested: isOldHarvested,
    };
  });

  // Apply OLD filter only if threshold > 0
  if (oldDaysThreshold > 0) {
    return enriched.filter((g) => g._isOldHarvested !== true);
  }

  return enriched;
};

const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

const Dropdowns = ({
  siteMetadata,
  setSiteMetadata,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  greenHouseLabel,
  setGreenHouseLabel,
  blockLabel,
  setBlockLabel,
  selectedGreenhouses,
  setSelectedGreenhouses,
  registerHarvestRefresh,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);
  const [greenhouseLoading, setGreenhouseLoading] = useState(false);

  const OLD_HARVEST_DAY_THRESHOLD = 45;

  const fetchGreenhouses = useCallback(
    async (orgId, siteId, cropId = null) => {
      setGreenhouseLoading(true);
      try {
        const res = await getGreenhouses(orgId, siteId, cropId);

        const greenhouseList = Array.isArray(res?.data?.data)
          ? res.data.data
          : [];

        let arr = greenhouseList.map((greenhouse) => ({
          name: greenhouse.name,
          id: greenhouse.id,
          plots: greenhouse.plots || [],
          metadata: greenhouse.metadata,
        }));

        const greenhouseIds = uniq(arr.map((g) => g.id));
        const plotIds = uniq(
          arr.flatMap((g) => (g.plots || []).map((p) => p.id)),
        );

        if (greenhouseIds.length && plotIds.length) {
          const resp = await getHarvestedPlots(
            orgId,
            siteId,
            cropId,
            greenhouseIds,
            plotIds,
          );

          arr = mergeHarvestIntoGreenhouses(arr, resp?.data?.data, {
            preserveExisting: false,
          });
        } else {
          arr = mergeHarvestIntoGreenhouses(arr, null, {
            preserveExisting: false,
          });
        }

        arr = decorateAndFilterOld(arr, OLD_HARVEST_DAY_THRESHOLD);
        arr.sort((a, b) => extractNumber(a.name) - extractNumber(b.name));

        setGreenhouseData(arr);

        // Default: select all after (re)fetch
        setSelectedGreenhouses(arr.map((g) => g.name));
      } finally {
        setGreenhouseLoading(false);
      }
    },
    [setGreenhouseData, setSelectedGreenhouses],
  );

  const refreshHarvested = useCallback(
    async ({ greenhouseIds: ghIdsArg, plotIds: pIdsArg } = {}) => {
      if (!currentOrg || !currentSite || !currentCrop) return;
      if (!greenhouseData?.length) return;

      const orgId = currentOrg?.id ?? currentOrg;
      const siteId = currentSite?.id ?? currentSite;
      const cropId = currentCrop?.id ?? currentCrop;

      const allGhIds = uniq(greenhouseData.map((g) => g.id));
      const allPlotIds = uniq(
        greenhouseData.flatMap((g) => (g.plots || []).map((p) => p.id)),
      );

      const greenhouseIds = uniq(ghIdsArg?.length ? ghIdsArg : allGhIds);
      const plotIds = uniq(pIdsArg?.length ? pIdsArg : allPlotIds);

      if (!greenhouseIds.length || !plotIds.length) return;

      const resp = await getHarvestedPlots(
        orgId,
        siteId,
        cropId,
        greenhouseIds,
        plotIds,
      );

      const ghSet = new Set(greenhouseIds);
      const pSet = new Set(plotIds);

      // keys we EXPECT to get an answer for (if missing => clear)
      const clearMissingKeys = new Set();
      for (const g of greenhouseData || []) {
        if (!ghSet.has(g.id)) continue;
        for (const p of g.plots || []) {
          if (!pSet.has(p.id)) continue;
          clearMissingKeys.add(`${g.id}-${p.id}`);
        }
      }

      // Merge subset response into existing greenhouseData without wiping other plots
      let next = mergeHarvestIntoGreenhouses(greenhouseData, resp?.data?.data, {
        preserveExisting: true,
        clearMissingKeys,
      });

      // Apply “old fully harvested removal” again (a refresh could change state)
      next = decorateAndFilterOld(next, OLD_HARVEST_DAY_THRESHOLD);

      next.sort((a, b) => extractNumber(a.name) - extractNumber(b.name));
      setGreenhouseData(next);

      // Keep selection sane: intersection with available GHs
      const available = new Set(next.map((g) => g.name));
      const prev = Array.isArray(selectedGreenhouses)
        ? selectedGreenhouses
        : [];
      const intersect = prev.filter((n) => available.has(n));
      setSelectedGreenhouses(
        intersect.length ? intersect : Array.from(available),
      );
    },
    [
      currentCrop,
      currentOrg,
      currentSite,
      greenhouseData,
      selectedGreenhouses,
      setGreenhouseData,
      setSelectedGreenhouses,
    ],
  );

  useEffect(() => {
    if (typeof registerHarvestRefresh === "function") {
      registerHarvestRefresh(refreshHarvested);
    }
  }, [registerHarvestRefresh, refreshHarvested]);

  // Fetch greenhouses on org/site/crop change
  useEffect(() => {
    if (currentSite && currentOrg && currentCrop) {
      const siteMetaData = currentSite.metadata[currentCrop.name] || {};
      setSiteMetadata(siteMetaData);

      if (siteMetaData?.greenhouse_name) {
        setGreenHouseLabel(siteMetaData.greenhouse_name);
      }
      if (siteMetaData?.block_name) {
        setBlockLabel(siteMetaData.block_name);
      }

      fetchGreenhouses(currentOrg, currentSite?.id, currentCrop?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSite, currentOrg, currentCrop]);

  // Set loading state
  useEffect(() => {
    setDropdownLoading(
      orgLoading || siteLoading || cropLoading || greenhouseLoading,
    );
  }, [
    orgLoading,
    siteLoading,
    cropLoading,
    greenhouseLoading,
    setDropdownLoading,
  ]);

  const greenhouseDropdownRender = (menu) => {
    if (!greenhouseData || greenhouseData.length === 0) return menu;

    const allGHNames = greenhouseData.map((g) => g.name);
    const allSelected =
      selectedGreenhouses &&
      selectedGreenhouses.length === greenhouseData.length;

    const toggleAll = () => {
      if (allSelected) setSelectedGreenhouses([]);
      else setSelectedGreenhouses(allGHNames);
    };

    return (
      <>
        <div
          style={{
            padding: "4px 12px",
            borderBottom: "1px solid #f0f0f0",
            cursor: "pointer",
            background: "#fff",
            fontWeight: 500,
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={toggleAll}
        >
          {allSelected ? "Deselect All" : "Select All"}
        </div>
        {menu}
      </>
    );
  };

  return (
    <>
      {orgLoading || siteLoading || greenhouseLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 mb-4 justify-between sm:flex-col dropdowns font-MuseoSans!">
          {/* Org Dropdown (ID-based) */}
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[150px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({ value: org.id, label: org.name }))}
            />
          )}

          {/* Site Dropdown (ID-based) */}
          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[150px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          {/* greenhouse dropdown is commented in your code; kept unchanged */}
        </div>
      )}
    </>
  );
};

export default Dropdowns;
